import React, { useState, useEffect } from 'react';
import {
  Users, Plus, Edit2, Trash2, X, Phone, Mail, Building2, Calendar,
  MessageSquare, ChevronDown, ChevronRight, Filter, Search, Clock,
  TrendingUp, Target, UserCheck, AlertCircle, CheckCircle, XCircle,
  Flame, ThermometerSun, Snowflake
} from 'lucide-react';
import { supabase } from '../supabase';
import { formatCurrency } from '../utils/commissionCalc';

const SUBSCRIPTION_TIERS = [
  { value: 'pro', label: 'Pro', price: 49 },
  { value: 'team', label: 'Team', price: 99 },
  { value: 'business', label: 'Business', price: 199 },
  { value: 'growth', label: 'Growth', price: 500 },
  { value: 'enterprise', label: 'Enterprise', price: 0 }
];

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: '#3498db', icon: Target },
  { value: 'contacted', label: 'Contacted', color: '#9b59b6', icon: MessageSquare },
  { value: 'qualified', label: 'Qualified', color: '#f39c12', icon: UserCheck },
  { value: 'negotiating', label: 'Negotiating', color: '#e67e22', icon: TrendingUp },
  { value: 'won', label: 'Won', color: '#27ae60', icon: CheckCircle },
  { value: 'lost', label: 'Lost', color: '#e74c3c', icon: XCircle }
];

const PRIORITY_OPTIONS = [
  { value: 'hot', label: 'Hot', color: '#e74c3c', icon: Flame },
  { value: 'warm', label: 'Warm', color: '#f39c12', icon: ThermometerSun },
  { value: 'cold', label: 'Cold', color: '#3498db', icon: Snowflake }
];

const CONTACT_TYPES = [
  { value: 'call', label: 'Phone Call' },
  { value: 'email', label: 'Email' },
  { value: 'text', label: 'Text Message' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'demo', label: 'Demo' },
  { value: 'other', label: 'Other' }
];

export default function LeadTracker() {
  const [leads, setLeads] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [expandedLeads, setExpandedLeads] = useState({});
  const [contactHistory, setContactHistory] = useState({});

  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterAffiliate, setFilterAffiliate] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('all'); // 'all', 'my', 'team'

  // Form state
  const [form, setForm] = useState({
    affiliate_id: '',
    company_name: '',
    contact_name: '',
    title: '',
    phone: '',
    email: '',
    subscription_tier: 'pro',
    user_count: 1,
    estimated_value: '',
    status: 'new',
    priority: 'warm',
    next_follow_up: '',
    notes: ''
  });

  // Contact form state
  const [contactForm, setContactForm] = useState({
    contact_type: 'call',
    notes: '',
    outcome: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load affiliates
      const { data: affiliatesData } = await supabase
        .from('affiliates')
        .select('id, name, code, parent_affiliate_id, can_recruit')
        .eq('active', true)
        .order('name');

      setAffiliates(affiliatesData || []);

      // Load leads with affiliate info
      const { data: leadsData } = await supabase
        .from('affiliate_leads')
        .select('*, affiliates(id, name, code, parent_affiliate_id)')
        .order('created_at', { ascending: false });

      setLeads(leadsData || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadContactHistory = async (leadId) => {
    const { data } = await supabase
      .from('affiliate_lead_contacts')
      .select('*, affiliates(name)')
      .eq('lead_id', leadId)
      .order('contacted_at', { ascending: false });

    setContactHistory(prev => ({
      ...prev,
      [leadId]: data || []
    }));
  };

  const toggleLeadExpand = async (leadId) => {
    const isExpanding = !expandedLeads[leadId];
    setExpandedLeads(prev => ({
      ...prev,
      [leadId]: isExpanding
    }));

    if (isExpanding && !contactHistory[leadId]) {
      await loadContactHistory(leadId);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const data = {
        affiliate_id: form.affiliate_id,
        company_name: form.company_name,
        contact_name: form.contact_name || null,
        title: form.title || null,
        phone: form.phone || null,
        email: form.email || null,
        subscription_tier: form.subscription_tier,
        user_count: parseInt(form.user_count) || 1,
        estimated_value: form.estimated_value ? parseFloat(form.estimated_value) : null,
        status: form.status,
        priority: form.priority,
        next_follow_up: form.next_follow_up || null,
        notes: form.notes || null,
        updated_at: new Date().toISOString()
      };

      if (editingLead) {
        await supabase.from('affiliate_leads').update(data).eq('id', editingLead.id);
      } else {
        await supabase.from('affiliate_leads').insert([data]);
      }

      setShowForm(false);
      setEditingLead(null);
      resetForm();
      loadData();
    } catch (error) {
      alert('Error saving lead: ' + error.message);
    }
  };

  const handleLogContact = async (e) => {
    e.preventDefault();

    try {
      // Insert contact record
      await supabase.from('affiliate_lead_contacts').insert([{
        lead_id: selectedLead.id,
        affiliate_id: selectedLead.affiliate_id,
        contact_type: contactForm.contact_type,
        notes: contactForm.notes || null,
        outcome: contactForm.outcome || null,
        contacted_at: new Date().toISOString()
      }]);

      // Update lead's last_contacted_at
      await supabase
        .from('affiliate_leads')
        .update({
          last_contacted_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedLead.id);

      // Refresh contact history for this lead
      await loadContactHistory(selectedLead.id);

      setShowContactForm(false);
      setContactForm({ contact_type: 'call', notes: '', outcome: '' });
      loadData();
    } catch (error) {
      alert('Error logging contact: ' + error.message);
    }
  };

  const handleEdit = (lead) => {
    setForm({
      affiliate_id: lead.affiliate_id,
      company_name: lead.company_name,
      contact_name: lead.contact_name || '',
      title: lead.title || '',
      phone: lead.phone || '',
      email: lead.email || '',
      subscription_tier: lead.subscription_tier || 'pro',
      user_count: lead.user_count || 1,
      estimated_value: lead.estimated_value?.toString() || '',
      status: lead.status,
      priority: lead.priority,
      next_follow_up: lead.next_follow_up || '',
      notes: lead.notes || ''
    });
    setEditingLead(lead);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this lead and all contact history?')) return;

    try {
      await supabase.from('affiliate_leads').delete().eq('id', id);
      loadData();
    } catch (error) {
      alert('Error deleting lead: ' + error.message);
    }
  };

  const handleStatusChange = async (lead, newStatus) => {
    try {
      await supabase
        .from('affiliate_leads')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', lead.id);
      loadData();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const resetForm = () => {
    setForm({
      affiliate_id: affiliates[0]?.id || '',
      company_name: '',
      contact_name: '',
      title: '',
      phone: '',
      email: '',
      subscription_tier: 'pro',
      user_count: 1,
      estimated_value: '',
      status: 'new',
      priority: 'warm',
      next_follow_up: '',
      notes: ''
    });
  };

  // Calculate estimated monthly value based on tier and user count
  const calculateValue = (tier, userCount) => {
    const tierData = SUBSCRIPTION_TIERS.find(t => t.value === tier);
    if (!tierData || tier === 'enterprise') return null;
    return tierData.price; // Monthly subscription price
  };

  // Get affiliates that the current affiliate can see (their team)
  const getTeamAffiliates = (affiliateId) => {
    return affiliates.filter(a => a.parent_affiliate_id === affiliateId);
  };

  // Filter leads
  const filteredLeads = leads.filter(lead => {
    if (filterStatus !== 'all' && lead.status !== filterStatus) return false;
    if (filterPriority !== 'all' && lead.priority !== filterPriority) return false;
    if (filterAffiliate !== 'all' && lead.affiliate_id !== filterAffiliate) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesCompany = lead.company_name?.toLowerCase().includes(search);
      const matchesContact = lead.contact_name?.toLowerCase().includes(search);
      const matchesEmail = lead.email?.toLowerCase().includes(search);
      if (!matchesCompany && !matchesContact && !matchesEmail) return false;
    }
    return true;
  });

  // Stats
  const stats = {
    total: leads.length,
    new: leads.filter(l => l.status === 'new').length,
    contacted: leads.filter(l => l.status === 'contacted').length,
    qualified: leads.filter(l => l.status === 'qualified').length,
    negotiating: leads.filter(l => l.status === 'negotiating').length,
    won: leads.filter(l => l.status === 'won').length,
    lost: leads.filter(l => l.status === 'lost').length,
    hot: leads.filter(l => l.priority === 'hot').length,
    pipelineValue: leads
      .filter(l => !['won', 'lost'].includes(l.status))
      .reduce((sum, l) => sum + (l.estimated_value || calculateValue(l.subscription_tier, l.user_count) || 0), 0),
    wonValue: leads
      .filter(l => l.status === 'won')
      .reduce((sum, l) => sum + (l.estimated_value || calculateValue(l.subscription_tier, l.user_count) || 0), 0)
  };

  // Group leads by affiliate for team view
  const leadsByAffiliate = affiliates.reduce((acc, affiliate) => {
    acc[affiliate.id] = leads.filter(l => l.affiliate_id === affiliate.id);
    return acc;
  }, {});

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTimeAgo = (date) => {
    if (!date) return 'Never';
    const now = new Date();
    const past = new Date(date);
    const diffMs = now - past;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  if (loading) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#888' }}>
        Loading leads...
      </div>
    );
  }

  const LeadCard = ({ lead }) => {
    const status = STATUS_OPTIONS.find(s => s.value === lead.status);
    const priority = PRIORITY_OPTIONS.find(p => p.value === lead.priority);
    const isExpanded = expandedLeads[lead.id];
    const contacts = contactHistory[lead.id] || [];
    const StatusIcon = status?.icon || Target;
    const PriorityIcon = priority?.icon || ThermometerSun;
    const estimatedValue = lead.estimated_value || calculateValue(lead.subscription_tier, lead.user_count);

    return (
      <div style={{
        background: '#1a1a1a',
        borderRadius: '12px',
        border: '1px solid #2a2a2a',
        overflow: 'hidden',
        marginBottom: '0.75rem'
      }}>
        {/* Main Card */}
        <div style={{ padding: '1rem' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '0.75rem',
            flexWrap: 'wrap',
            gap: '0.75rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', flex: 1 }}>
              {/* Expand button */}
              <button
                onClick={() => toggleLeadExpand(lead.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#888',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  marginTop: '0.25rem'
                }}
              >
                {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              </button>

              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <h3 style={{
                    fontSize: '1.1rem',
                    fontWeight: '700',
                    color: '#e0e0e0',
                    margin: 0
                  }}>
                    {lead.company_name}
                  </h3>

                  {/* Status badge */}
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    padding: '0.2rem 0.5rem',
                    background: `${status?.color}20`,
                    color: status?.color,
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: '600'
                  }}>
                    <StatusIcon size={12} />
                    {status?.label}
                  </span>

                  {/* Priority badge */}
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    padding: '0.2rem 0.5rem',
                    background: `${priority?.color}20`,
                    color: priority?.color,
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: '600'
                  }}>
                    <PriorityIcon size={12} />
                    {priority?.label}
                  </span>
                </div>

                {/* Contact info row */}
                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  fontSize: '0.85rem',
                  color: '#888',
                  marginTop: '0.5rem',
                  flexWrap: 'wrap'
                }}>
                  {lead.contact_name && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <Users size={14} />
                      {lead.contact_name}{lead.title && ` - ${lead.title}`}
                    </span>
                  )}
                  {lead.phone && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <Phone size={14} />
                      {lead.phone}
                    </span>
                  )}
                  {lead.email && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <Mail size={14} />
                      {lead.email}
                    </span>
                  )}
                </div>

                {/* Affiliate tag */}
                <div style={{
                  marginTop: '0.5rem',
                  fontSize: '0.75rem',
                  color: '#666'
                }}>
                  Assigned to: <span style={{ color: '#ff6b35' }}>{lead.affiliates?.name}</span>
                </div>
              </div>
            </div>

            {/* Right side - Stats & Actions */}
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
              <button
                onClick={() => {
                  setSelectedLead(lead);
                  setShowContactForm(true);
                }}
                title="Log contact"
                style={{
                  padding: '0.5rem',
                  background: '#4ecca320',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#4ecca3',
                  cursor: 'pointer'
                }}
              >
                <MessageSquare size={16} />
              </button>
              <button
                onClick={() => handleEdit(lead)}
                style={{
                  padding: '0.5rem',
                  background: '#2a2a2a',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#888',
                  cursor: 'pointer'
                }}
              >
                <Edit2 size={16} />
              </button>
              <button
                onClick={() => handleDelete(lead.id)}
                style={{
                  padding: '0.5rem',
                  background: '#2a1a1a',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#e74c3c',
                  cursor: 'pointer'
                }}
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>

          {/* Stats row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
            gap: '0.5rem'
          }}>
            <div style={{
              background: '#242424',
              borderRadius: '6px',
              padding: '0.5rem 0.75rem'
            }}>
              <div style={{ fontSize: '0.65rem', color: '#666', textTransform: 'uppercase' }}>Tier</div>
              <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#e0e0e0' }}>
                {SUBSCRIPTION_TIERS.find(t => t.value === lead.subscription_tier)?.label || 'Pro'}
              </div>
            </div>
            <div style={{
              background: '#242424',
              borderRadius: '6px',
              padding: '0.5rem 0.75rem'
            }}>
              <div style={{ fontSize: '0.65rem', color: '#666', textTransform: 'uppercase' }}>Users</div>
              <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#e0e0e0' }}>
                {lead.user_count || 1}
              </div>
            </div>
            <div style={{
              background: '#242424',
              borderRadius: '6px',
              padding: '0.5rem 0.75rem'
            }}>
              <div style={{ fontSize: '0.65rem', color: '#666', textTransform: 'uppercase' }}>Value</div>
              <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#4ecca3' }}>
                {estimatedValue ? formatCurrency(estimatedValue) + '/mo' : '-'}
              </div>
            </div>
            <div style={{
              background: '#242424',
              borderRadius: '6px',
              padding: '0.5rem 0.75rem'
            }}>
              <div style={{ fontSize: '0.65rem', color: '#666', textTransform: 'uppercase' }}>Last Contact</div>
              <div style={{ fontSize: '0.9rem', fontWeight: '600', color: lead.last_contacted_at ? '#e0e0e0' : '#666' }}>
                {formatTimeAgo(lead.last_contacted_at)}
              </div>
            </div>
            {lead.next_follow_up && (
              <div style={{
                background: '#242424',
                borderRadius: '6px',
                padding: '0.5rem 0.75rem'
              }}>
                <div style={{ fontSize: '0.65rem', color: '#666', textTransform: 'uppercase' }}>Follow-up</div>
                <div style={{
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  color: new Date(lead.next_follow_up) < new Date() ? '#e74c3c' : '#f39c12'
                }}>
                  {formatDate(lead.next_follow_up)}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Expanded section - Contact history */}
        {isExpanded && (
          <div style={{
            borderTop: '1px solid #2a2a2a',
            padding: '1rem',
            background: '#151515'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '0.75rem'
            }}>
              <h4 style={{ margin: 0, fontSize: '0.9rem', color: '#e0e0e0' }}>
                Contact History
              </h4>
              <button
                onClick={() => {
                  setSelectedLead(lead);
                  setShowContactForm(true);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  padding: '0.4rem 0.75rem',
                  background: '#4ecca320',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#4ecca3',
                  fontSize: '0.8rem',
                  cursor: 'pointer'
                }}
              >
                <Plus size={14} />
                Log Contact
              </button>
            </div>

            {contacts.length === 0 ? (
              <div style={{ color: '#666', fontSize: '0.85rem', textAlign: 'center', padding: '1rem' }}>
                No contact history yet
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {contacts.map(contact => (
                  <div key={contact.id} style={{
                    background: '#1a1a1a',
                    borderRadius: '8px',
                    padding: '0.75rem',
                    borderLeft: `3px solid ${
                      contact.contact_type === 'call' ? '#3498db' :
                      contact.contact_type === 'email' ? '#9b59b6' :
                      contact.contact_type === 'meeting' ? '#27ae60' :
                      contact.contact_type === 'demo' ? '#f39c12' :
                      '#888'
                    }`
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '0.35rem'
                    }}>
                      <span style={{
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        color: '#e0e0e0',
                        textTransform: 'capitalize'
                      }}>
                        {CONTACT_TYPES.find(t => t.value === contact.contact_type)?.label || contact.contact_type}
                      </span>
                      <span style={{ fontSize: '0.75rem', color: '#666' }}>
                        {formatDate(contact.contacted_at)}
                      </span>
                    </div>
                    {contact.notes && (
                      <div style={{ fontSize: '0.85rem', color: '#aaa', marginBottom: '0.25rem' }}>
                        {contact.notes}
                      </div>
                    )}
                    {contact.outcome && (
                      <div style={{ fontSize: '0.8rem', color: '#4ecca3' }}>
                        Outcome: {contact.outcome}
                      </div>
                    )}
                    <div style={{ fontSize: '0.7rem', color: '#666', marginTop: '0.25rem' }}>
                      By: {contact.affiliates?.name}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Notes */}
            {lead.notes && (
              <div style={{
                marginTop: '0.75rem',
                padding: '0.75rem',
                background: '#1a1a1a',
                borderRadius: '8px',
                fontSize: '0.85rem',
                color: '#aaa'
              }}>
                <strong style={{ color: '#888' }}>Notes:</strong> {lead.notes}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: '1.5rem' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <h1 style={{
            fontSize: '1.75rem',
            fontWeight: '700',
            color: '#e0e0e0',
            marginBottom: '0.5rem'
          }}>
            Lead Tracker
          </h1>
          <p style={{ color: '#888', fontSize: '0.9rem' }}>
            Track and manage sales leads for affiliates
          </p>
        </div>

        <button
          onClick={() => {
            resetForm();
            setEditingLead(null);
            setShowForm(true);
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1.25rem',
            background: '#ff6b35',
            border: 'none',
            borderRadius: '8px',
            color: '#fff',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          <Plus size={18} />
          Add Lead
        </button>
      </div>

      {/* Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{
          background: '#1a1a1a',
          borderRadius: '10px',
          padding: '1rem',
          border: '1px solid #2a2a2a',
          borderLeft: '4px solid #3498db'
        }}>
          <div style={{ color: '#888', fontSize: '0.75rem', marginBottom: '0.25rem', textTransform: 'uppercase' }}>
            Total Leads
          </div>
          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#3498db' }}>
            {stats.total}
          </div>
        </div>
        <div style={{
          background: '#1a1a1a',
          borderRadius: '10px',
          padding: '1rem',
          border: '1px solid #2a2a2a',
          borderLeft: '4px solid #e74c3c'
        }}>
          <div style={{ color: '#888', fontSize: '0.75rem', marginBottom: '0.25rem', textTransform: 'uppercase' }}>
            Hot Leads
          </div>
          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#e74c3c' }}>
            {stats.hot}
          </div>
        </div>
        <div style={{
          background: '#1a1a1a',
          borderRadius: '10px',
          padding: '1rem',
          border: '1px solid #2a2a2a',
          borderLeft: '4px solid #f39c12'
        }}>
          <div style={{ color: '#888', fontSize: '0.75rem', marginBottom: '0.25rem', textTransform: 'uppercase' }}>
            In Pipeline
          </div>
          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#f39c12' }}>
            {stats.new + stats.contacted + stats.qualified + stats.negotiating}
          </div>
        </div>
        <div style={{
          background: '#1a1a1a',
          borderRadius: '10px',
          padding: '1rem',
          border: '1px solid #2a2a2a',
          borderLeft: '4px solid #27ae60'
        }}>
          <div style={{ color: '#888', fontSize: '0.75rem', marginBottom: '0.25rem', textTransform: 'uppercase' }}>
            Won
          </div>
          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#27ae60' }}>
            {stats.won}
          </div>
        </div>
        <div style={{
          background: '#1a1a1a',
          borderRadius: '10px',
          padding: '1rem',
          border: '1px solid #2a2a2a',
          borderLeft: '4px solid #4ecca3'
        }}>
          <div style={{ color: '#888', fontSize: '0.75rem', marginBottom: '0.25rem', textTransform: 'uppercase' }}>
            Pipeline Value
          </div>
          <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#4ecca3' }}>
            {formatCurrency(stats.pipelineValue)}/mo
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: '0.75rem',
        marginBottom: '1rem',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        {/* Search */}
        <div style={{
          position: 'relative',
          flex: '1',
          minWidth: '200px',
          maxWidth: '300px'
        }}>
          <Search size={16} style={{
            position: 'absolute',
            left: '0.75rem',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#666'
          }} />
          <input
            type="text"
            placeholder="Search leads..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '0.6rem 0.75rem 0.6rem 2.25rem',
              background: '#1a1a1a',
              border: '1px solid #2a2a2a',
              borderRadius: '8px',
              color: '#e0e0e0',
              fontSize: '0.9rem'
            }}
          />
        </div>

        {/* Status filter */}
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={{
            padding: '0.6rem 0.75rem',
            background: '#1a1a1a',
            border: '1px solid #2a2a2a',
            borderRadius: '8px',
            color: '#e0e0e0',
            fontSize: '0.9rem'
          }}
        >
          <option value="all">All Statuses</option>
          {STATUS_OPTIONS.map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>

        {/* Priority filter */}
        <select
          value={filterPriority}
          onChange={(e) => setFilterPriority(e.target.value)}
          style={{
            padding: '0.6rem 0.75rem',
            background: '#1a1a1a',
            border: '1px solid #2a2a2a',
            borderRadius: '8px',
            color: '#e0e0e0',
            fontSize: '0.9rem'
          }}
        >
          <option value="all">All Priorities</option>
          {PRIORITY_OPTIONS.map(p => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
        </select>

        {/* Affiliate filter */}
        <select
          value={filterAffiliate}
          onChange={(e) => setFilterAffiliate(e.target.value)}
          style={{
            padding: '0.6rem 0.75rem',
            background: '#1a1a1a',
            border: '1px solid #2a2a2a',
            borderRadius: '8px',
            color: '#e0e0e0',
            fontSize: '0.9rem'
          }}
        >
          <option value="all">All Affiliates</option>
          {affiliates.map(a => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>
      </div>

      {/* Leads List */}
      <div>
        {filteredLeads.length === 0 ? (
          <div style={{
            background: '#1a1a1a',
            borderRadius: '12px',
            border: '1px solid #2a2a2a',
            padding: '3rem',
            textAlign: 'center',
            color: '#888'
          }}>
            <Target size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
            <p>No leads found. Add your first lead to get started.</p>
          </div>
        ) : (
          filteredLeads.map(lead => (
            <LeadCard key={lead.id} lead={lead} />
          ))
        )}
      </div>

      {/* Add/Edit Lead Modal */}
      {showForm && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#1a1a1a',
            borderRadius: '12px',
            border: '1px solid #3a3a3a',
            padding: '1.5rem',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.25rem'
            }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#e0e0e0' }}>
                {editingLead ? 'Edit Lead' : 'Add New Lead'}
              </h2>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingLead(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#888',
                  cursor: 'pointer'
                }}
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Company & Assignment */}
              <div style={{
                background: '#242424',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <h3 style={{ color: '#e0e0e0', fontSize: '0.9rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Building2 size={16} /> Company Info
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                      Company Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={form.company_name}
                      onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: '#1a1a1a',
                        border: '1px solid #3a3a3a',
                        borderRadius: '6px',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                      Assigned To *
                    </label>
                    <select
                      required
                      value={form.affiliate_id}
                      onChange={(e) => setForm({ ...form, affiliate_id: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: '#1a1a1a',
                        border: '1px solid #3a3a3a',
                        borderRadius: '6px',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}
                    >
                      <option value="">Select affiliate...</option>
                      {affiliates.map(a => (
                        <option key={a.id} value={a.id}>{a.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div style={{
                background: '#242424',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <h3 style={{ color: '#e0e0e0', fontSize: '0.9rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Users size={16} /> Contact Info
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                      Contact Name
                    </label>
                    <input
                      type="text"
                      value={form.contact_name}
                      onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: '#1a1a1a',
                        border: '1px solid #3a3a3a',
                        borderRadius: '6px',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                      Title
                    </label>
                    <input
                      type="text"
                      value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                      placeholder="e.g., Owner, Manager"
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: '#1a1a1a',
                        border: '1px solid #3a3a3a',
                        borderRadius: '6px',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                      Phone
                    </label>
                    <input
                      type="text"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: '#1a1a1a',
                        border: '1px solid #3a3a3a',
                        borderRadius: '6px',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                      Email
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: '#1a1a1a',
                        border: '1px solid #3a3a3a',
                        borderRadius: '6px',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Deal Info */}
              <div style={{
                background: '#242424',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <h3 style={{ color: '#e0e0e0', fontSize: '0.9rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <TrendingUp size={16} /> Deal Info
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                      Subscription Tier
                    </label>
                    <select
                      value={form.subscription_tier}
                      onChange={(e) => setForm({ ...form, subscription_tier: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: '#1a1a1a',
                        border: '1px solid #3a3a3a',
                        borderRadius: '6px',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}
                    >
                      {SUBSCRIPTION_TIERS.map(t => (
                        <option key={t.value} value={t.value}>
                          {t.label} {t.price > 0 ? `(${formatCurrency(t.price)}/mo)` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                      User Count
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={form.user_count}
                      onChange={(e) => setForm({ ...form, user_count: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: '#1a1a1a',
                        border: '1px solid #3a3a3a',
                        borderRadius: '6px',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                      Est. Value ($/mo)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.estimated_value}
                      onChange={(e) => setForm({ ...form, estimated_value: e.target.value })}
                      placeholder="Auto-calculated"
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: '#1a1a1a',
                        border: '1px solid #3a3a3a',
                        borderRadius: '6px',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                      Status
                    </label>
                    <select
                      value={form.status}
                      onChange={(e) => setForm({ ...form, status: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: '#1a1a1a',
                        border: '1px solid #3a3a3a',
                        borderRadius: '6px',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}
                    >
                      {STATUS_OPTIONS.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                      Priority
                    </label>
                    <select
                      value={form.priority}
                      onChange={(e) => setForm({ ...form, priority: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: '#1a1a1a',
                        border: '1px solid #3a3a3a',
                        borderRadius: '6px',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}
                    >
                      {PRIORITY_OPTIONS.map(p => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                      Next Follow-up
                    </label>
                    <input
                      type="date"
                      value={form.next_follow_up}
                      onChange={(e) => setForm({ ...form, next_follow_up: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: '#1a1a1a',
                        border: '1px solid #3a3a3a',
                        borderRadius: '6px',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                  Notes
                </label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '0.6rem',
                    background: '#242424',
                    border: '1px solid #3a3a3a',
                    borderRadius: '6px',
                    color: '#e0e0e0',
                    fontSize: '0.9rem',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingLead(null);
                  }}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: '#2a2a2a',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#e0e0e0',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 2,
                    padding: '0.75rem',
                    background: '#ff6b35',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  {editingLead ? 'Save Changes' : 'Add Lead'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Log Contact Modal */}
      {showContactForm && selectedLead && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#1a1a1a',
            borderRadius: '12px',
            border: '1px solid #3a3a3a',
            padding: '1.5rem',
            maxWidth: '450px',
            width: '100%'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.25rem'
            }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#e0e0e0' }}>
                Log Contact
              </h2>
              <button
                onClick={() => {
                  setShowContactForm(false);
                  setSelectedLead(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#888',
                  cursor: 'pointer'
                }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{
              padding: '0.75rem',
              background: '#242424',
              borderRadius: '8px',
              marginBottom: '1rem'
            }}>
              <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#e0e0e0' }}>
                {selectedLead.company_name}
              </div>
              {selectedLead.contact_name && (
                <div style={{ fontSize: '0.8rem', color: '#888' }}>
                  {selectedLead.contact_name}
                </div>
              )}
            </div>

            <form onSubmit={handleLogContact}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                  Contact Type
                </label>
                <select
                  value={contactForm.contact_type}
                  onChange={(e) => setContactForm({ ...contactForm, contact_type: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.6rem',
                    background: '#242424',
                    border: '1px solid #3a3a3a',
                    borderRadius: '6px',
                    color: '#e0e0e0',
                    fontSize: '0.9rem'
                  }}
                >
                  {CONTACT_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                  Notes
                </label>
                <textarea
                  value={contactForm.notes}
                  onChange={(e) => setContactForm({ ...contactForm, notes: e.target.value })}
                  rows={3}
                  placeholder="What was discussed?"
                  style={{
                    width: '100%',
                    padding: '0.6rem',
                    background: '#242424',
                    border: '1px solid #3a3a3a',
                    borderRadius: '6px',
                    color: '#e0e0e0',
                    fontSize: '0.9rem',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                  Outcome
                </label>
                <input
                  type="text"
                  value={contactForm.outcome}
                  onChange={(e) => setContactForm({ ...contactForm, outcome: e.target.value })}
                  placeholder="e.g., Scheduled demo, Left voicemail"
                  style={{
                    width: '100%',
                    padding: '0.6rem',
                    background: '#242424',
                    border: '1px solid #3a3a3a',
                    borderRadius: '6px',
                    color: '#e0e0e0',
                    fontSize: '0.9rem'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowContactForm(false);
                    setSelectedLead(null);
                  }}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: '#2a2a2a',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#e0e0e0',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 2,
                    padding: '0.75rem',
                    background: '#4ecca3',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Log Contact
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
